import { Button, Checkbox, Select } from "@shopify/polaris"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import convertCurrency from "../../utils/convertCurrency.js"
import { roundPrice } from "../PriceRounding.jsx"
import { ProductsSelectorFilter } from "./ProductsSelectorFilter.jsx"
import ProductsSelectorRow from "./ProductsSelectorRow.jsx"
import { useTranslation } from "react-i18next"

const PRODUCTS_PAGE_SIZE = 100

export const ProductsSelector = ({
	products,
	selectedProductsIds,
	setSelectedProductsIds,
	roundingEnabled,
	roundingNumber,
	storeCurrency,
}) => {
	const [productsToDisplay, setProductsToDisplay] = useState([])
	const [query, setQuery] = useState(null)
	const [sortBy, setSortBy] = useState("default")
	const [selectAllEnabled, setSelectAllEnabled] = useState(false)
	const { t } = useTranslation()

	const filterProducts = (products, query) => {
		if (!query) return products

		return products.filter((product) => {
			const includesTitle = product?.title
				?.toLowerCase()
				.includes(query.toLowerCase())

			const productVariantPrice = product?.variants?.[0]?.price || null
			const productVariantPriceNumber =
				typeof productVariantPrice === "string" &&
				productVariantPrice !== ""
					? parseFloat(product.variants[0].price)
					: typeof productVariantPrice === "number"
						? product.variants[0].price
						: 0

			const includesPrice = productVariantPrice
				? convertCurrency(
						productVariantPriceNumber,
						product._currency,
						storeCurrency,
					)
						.toFixed(2)
						.includes(query)
				: false

			return includesTitle || includesPrice
		})
	}

	const sortProducts = (products, sortBy) => {
		if (sortBy === "default") return products

		// Copy first: when there is no query, filterProducts returns the same
		// array it was given, so sorting in place would mutate state.
		return [...products].sort((a, b) => {
			const priceA = parseFloat(a.variants?.[0]?.price) || 0
			const priceB = parseFloat(b.variants?.[0]?.price) || 0

			switch (sortBy) {
				case "newest_created":
					return new Date(b.created_at) - new Date(a.created_at)
				case "oldest_created":
					return new Date(a.created_at) - new Date(b.created_at)
				case "a_z":
					if (a.title < b.title) {
						return -1
					} else if (a.title > b.title) {
						return 1
					} else {
						return 0
					}
				case "z_a":
					if (a.title < b.title) {
						return 1
					} else if (a.title > b.title) {
						return -1
					} else {
						return 0
					}
				case "price_ascending":
					if (priceA < priceB) {
						return -1
					} else if (priceA > priceB) {
						return 1
					} else {
						return 0
					}
				case "price_descending":
					if (priceA < priceB) {
						return 1
					} else if (priceA > priceB) {
						return -1
					} else {
						return 0
					}
			}
		})
	}

	const onToggleSelectAllEnabled = (change) => {
		setSelectAllEnabled(change)

		if (change) {
			setSelectedProductsIds(
				filteredProducts.map((product) => product.id),
			)
		} else {
			setSelectedProductsIds([])
		}
	}

	useEffect(() => {
		setProductsToDisplay(
			products.map((product) => {
				const price = product?.variants?.[0]?.price || null
				const priceNumber =
					typeof price === "string" && price !== ""
						? parseFloat(product.variants[0].price)
						: typeof price === "number"
							? product.variants[0].price
							: 0
				const originalPriceString = `${product._currency} ${priceNumber?.toFixed(2) || ""}`

				const convertedPriceNumber =
					convertCurrency(
						priceNumber,
						product._currency,
						storeCurrency,
					) || 0
				const convertedPriceString = `${storeCurrency} ${convertedPriceNumber?.toFixed(2) || ""}`

				const roundedConvertedPriceNumber = roundPrice(
					convertedPriceNumber,
					roundingNumber,
				)
				const roundedConvertedPriceString =
					roundingEnabled && roundingNumber !== null
						? `${storeCurrency} ${roundedConvertedPriceNumber?.toFixed(2) || ""}`
						: convertedPriceString

				const tags = Array.isArray(product.tags)
					? product.tags
					: typeof product.tags === "string"
						? product.tags.split(", ").filter((tag) => tag !== "")
						: []

				return {
					...product,
					originalPrice: originalPriceString,
					convertedPrice: convertedPriceString,
					roundedConvertedPrice: roundedConvertedPriceString,
					tags,
				}
			}),
		)
	}, [products, roundingEnabled, roundingNumber])

	const filteredProducts = useMemo(
		() => filterProducts(productsToDisplay, query),
		[productsToDisplay, query],
	)
	const sortedProducts = useMemo(
		() => sortProducts(filteredProducts, sortBy),
		[filteredProducts, sortBy],
	)

	// Membership was an Array#includes per row, so a 1000-product select-all cost
	// a million comparisons on every render.
	const selectedIdsSet = useMemo(
		() => new Set(selectedProductsIds),
		[selectedProductsIds],
	)

	// Each row mounts several Polaris components (including a Tooltip), so
	// rendering a whole large import at once locks up the browser. Reveal them a
	// page at a time instead; selection and the counts above still cover the full
	// filtered set, not just what is on screen.
	const [visibleCount, setVisibleCount] = useState(PRODUCTS_PAGE_SIZE)

	useEffect(() => {
		setVisibleCount(PRODUCTS_PAGE_SIZE)
	}, [query, sortBy, productsToDisplay])

	const visibleProducts = useMemo(
		() => sortedProducts.slice(0, visibleCount),
		[sortedProducts, visibleCount],
	)
	const remainingCount = sortedProducts.length - visibleProducts.length

	const showMore = useCallback(
		() =>
			setVisibleCount((current) =>
				Math.min(current + PRODUCTS_PAGE_SIZE, sortedProducts.length),
			),
		[sortedProducts.length],
	)

	// Reveal the next page as the sentinel scrolls into view, so the list behaves
	// like a normal long list; the button below it is the manual fallback.
	const sentinelRef = useRef(null)
	useEffect(() => {
		if (remainingCount <= 0) return
		if (typeof IntersectionObserver === "undefined") return

		const sentinel = sentinelRef.current
		if (!sentinel) return

		const observer = new IntersectionObserver((entries) => {
			if (entries.some((entry) => entry.isIntersecting)) showMore()
		})
		observer.observe(sentinel)
		return () => observer.disconnect()
	}, [remainingCount, showMore])

	return (
		<div>
			<div style={{ padding: "1rem" }}>
				<div style={{ marginBottom: "1rem" }}>
					<ProductsSelectorFilter setQuery={setQuery} />
				</div>

				<div
					style={{
						display: "flex",
						alignItems: "center",
						gap: "0.5rem",
					}}
				>
					<Checkbox
						checked={selectAllEnabled}
						onChange={(change) => onToggleSelectAllEnabled(change)}
					/>
					<p style={{ fontWeight: "600" }}>
						{t("multiple.showing")} {filteredProducts.length}/
						{productsToDisplay.length} {t("multiple.products")}
					</p>

					<div style={{ marginLeft: "auto" }}>
						<Select
							options={[
								{
									label: t("common.default"),
									value: "default",
								},
								{
									label: t("multiple.newest_created_first"),
									value: "newest_created",
								},
								{
									label: t("multiple.oldest_created_first"),
									value: "oldest_created",
								},
								{ label: "A-Z", value: "a_z" },
								{ label: "Z-A", value: "z_a" },
								{
									label: t("multiple.cheapest_first"),
									value: "price_ascending",
								},
								{
									label: t("multiple.most_expensive_first"),
									value: "price_descending",
								},
							]}
							value={sortBy}
							onChange={(change) => setSortBy(change)}
						/>
					</div>
				</div>
			</div>

			<div style={{ borderTop: "1px solid #ccc" }}>
				{visibleProducts.map((product, index) => {
					return (
						<ProductsSelectorRow
							key={`${product.id}-${index}`}
							product={product}
							roundingEnabled={roundingEnabled}
							checked={selectedIdsSet.has(product.id)}
							setSelectedProductsIds={setSelectedProductsIds}
						/>
					)
				})}

				{remainingCount > 0 && (
					<div
						ref={sentinelRef}
						style={{ padding: "1rem", textAlign: "center" }}
					>
						<Button onClick={showMore}>
							{t("multiple.show")} {remainingCount}{" "}
							{t("multiple.products")}
						</Button>
					</div>
				)}
			</div>
		</div>
	)
}
